
import { Component, Vue } from 'vue-property-decorator'
import Project from "@/types/project/project"
import Tracking from "@/types/tracking/tracking"
import { TimesService } from "@/services/timetrack/TimesService"
import { ProjectService } from "@/services/timetrack/ProjectService"
import ConfirmDialog from "@/components/ConfirmDialog.vue"
import { BillingTypes } from "@/types/project/billing_types"
import { ExpenditureService } from "@/services/timetrack/ExpenditureService"
import ExpenditureList from "@/components/Expenditure/ExpenditureList.vue"
import { DateAndTimeHelper } from "@/helpers/DateAndTimeHelper"
import CollaboratorDashboard from "@/components/Collaborator/CollaboratorDashboard.vue"
import DeleteProjectDialog from "@/components/Project/DeleteProjectDialog.vue"
import { CollaboratorService } from "@/services/timetrack/CollaboratorService"
import { CollaborationState } from "@/types/collaborator/Collaborator"

@Component({
  filters: {
    formatCurrency (value: any) {
      return Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
      }).format(value)
    }
  },
  components: {
    DeleteProjectDialog,
    CollaboratorDashboard,
    ExpenditureList,
    ConfirmDialog
  }
})
export default class ProjectView extends Vue {
  private valid = false
  private removeProjectDialog = false
  private timesLoading = false
  private collabDialog = false
  private times: Tracking[] | null = null
  private project: Project = this.$store.getters['project/project']
  private changeableProject: Project | null = null
  private hasRunningTracking: boolean | null = this.$store.getters['project/hasRunningTracking']
  private timeService = new TimesService()
  private projectService = new ProjectService()
  private collaboratorService = new CollaboratorService()
  private expenditureService = new ExpenditureService()
  private dateAndTimeHelper = new DateAndTimeHelper()

  private timesHeaders = [
    { text: this.$t('timetrack.project.date'), value: 'startingTime', sortable: true },
    { text: this.$t('timetrack.project.task'), value: 'task', sortable: false, align: 'center' },
    { text: this.$t('timetrack.project.tracked'), value: 'endTime', sortable: false, align: 'center' },
    { text: this.$t('timetrack.project.salary'), value: 'making', sortable: false, align: 'end' },
    { text: this.$t('timetrack.expenditure.totalPrice'), value: 'totalExpenditurePrice', sortable: false, align: 'end' },
    { text: this.$t('timetrack.projects.projectdata.actions'), value: 'actions', sortable: false, align: this.$vuetify.breakpoint.mdAndDown ? ' d-none' : 'end' }
  ]

  private calculateTrackedTime (index: number) {
    if (!this.project || !this.project.trackedTimes) return 0
    const startingTime = this.project.trackedTimes[index].startingTime
    const endTime = this.project.trackedTimes[index].endTime
    const date = new Date(Number(endTime) - Number(startingTime))
    date.setTime(date.getTime() + date.getTimezoneOffset() * 60 * 1000)
    return this.dateAndTimeHelper.twoDigits(date.getHours()) + ':' + this.dateAndTimeHelper.twoDigits(date.getMinutes()) + ':' + this.dateAndTimeHelper.twoDigits(date.getSeconds())
  }

  private readableDate (startingTime: number) {
    return this.dateAndTimeHelper.getReadableDateFromStartingTime(startingTime)
  }

  private notEmptyRule = [
    (v: any) => !!v || this.$t('global.required')
  ]

  private get billingTypes () {
    const types = []
    for (const type in BillingTypes) {
      types.push(
        {
          value: type.toLowerCase(),
          translation: this.$t('timetrack.projects.projectdata.' + type.toLowerCase())
        })
    }
    return types
  }

  private expenditureTotalPrice (item: Tracking) {
    let expenditureTotalPrice = 0
    if (!item || !item.expenditures) return expenditureTotalPrice
    item.expenditures.forEach((expenditure) => {
      expenditureTotalPrice += expenditure.totalPrice || 0
    })
    return expenditureTotalPrice
  }

  private get paymentLabel () {
    return this.project.billingType === BillingTypes.HOURLY
      ? this.$t('timetrack.projects.projectdata.hourlypayment')
      : this.$t('timetrack.projects.projectdata.fixedpayment')
  }

  private get projectHasChanged () {
    return !(
      this.changeableProject?.name !== this.project.name ||
      this.changeableProject?.description !== this.project.description ||
      this.changeableProject?.billingType !== this.project.billingType ||
      this.changeableProject?.salary !== this.project.salary
    ) || !this.valid
  }

  created () {
    this.fetchProjectOrTrackedTimes(this.$route.params.id)
  }

  private fetchProjectOrTrackedTimes (projectId?: string, forceProjectFetch?: boolean) {
    this.timesLoading = true
    if (this.project && this.project.id && !forceProjectFetch) {
      this.fetchTrackedTimes(this.project.id)
        .then(() => {
          this.fetchExpenditures()
          this.changeableProject = JSON.parse(JSON.stringify(this.project))
        })
    } else if (projectId) {
      this.fetchProject(projectId)
        .then(() => {
          this.fetchTrackedTimes(projectId)
            .then(() => {
              this.fetchExpenditures()
              this.changeableProject = JSON.parse(JSON.stringify(this.project))
            })
        })
    } else {
      this.$router.push('/timetrack')
    }
  }

  private fetchProject (projectId: string) {
    return this.projectService.getProject(projectId)
      .then((project) => {
        if (project) {
          this.project = project
          this.changeableProject = JSON.parse(JSON.stringify(this.project))
          this.$store.dispatch('project/setProject', this.project)
        }
      })
      .catch((error) => {
        this.$store.dispatch('error/setError', error)
      })
  }

  private fetchTrackedTimes (projectId: string) {
    return this.timeService.getAllForProject(projectId)
      .then((times) => {
        if (times && times.length > 0) {
          this.times = times
          // TODO making needs to be updated for every Time if salary changes
        } else {
          this.project.trackedTimes = []
        }
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.timesLoading = false
      })
  }

  private fetchExpenditures () {
    if (!this.project || !this.times) return
    const trackingIds = this.times.map((tracking) => { return tracking.id })
    if (!trackingIds || trackingIds.length < 1) return
    this.expenditureService.getExpendituresForTrackings(trackingIds as string[])
      .then((expenditures) => {
        if (expenditures && this.times) {
          this.times = this.times.map((tracking) => {
            tracking.expenditures = expenditures.filter((expenditure) => { return expenditure.trackingId === tracking.id })
            tracking.totalExpenditurePrice = this.expenditureTotalPrice(tracking)
            return tracking
          })
          this.project.trackedTimes = this.times
          this.$store.dispatch('project/setProject', this.project)
        }
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.timesLoading = false
      })
  }

  private deleteProject () {
    if (this.project && this.project.id) {
      this.timesLoading = true
      this.timeService.deleteTrackedTimesForProject(this.project.id)
        .then(() => {
          this.removeExpenditures()
          if (this.project.id) {
            this.projectService.deleteProject(this.project.id)
              .then(() => {
                if (!this.project || !this.project.id) return
                this.collaboratorService.getCollaboratorsFromProject(this.project.id)
                  .then((collaborators) => {
                    collaborators.forEach((collab) => {
                      if (!collab || !collab.id) return
                      this.collaboratorService.setCollaboratorState(collab.id, CollaborationState.delete)
                    })
                  })
              })
          }
        })
        .catch((error) => {
          this.$store.dispatch('error/setError', error)
        }).finally(() => {
          this.timesLoading = false
          this.$store.dispatch('project/setProject', null)
          this.$router.push('/timetrack')
        })
    }
  }

  private removeExpenditures () {
    if (this.project.trackedTimes) {
      const trackingIds = this.project.trackedTimes.map((tracking) => { return tracking.id })
      if (trackingIds && trackingIds.length > 0) {
        this.expenditureService.removeExpendituresForTrackings(trackingIds as string[])
      }
    }
  }

  private updateProject () {
    const updateMakingsOfTrackings = this.project.salary !== this.changeableProject?.salary
    this.timesLoading = true
    if (!this.changeableProject || !this.changeableProject.id) return
    this.projectService.updateProject(this.changeableProject?.id, this.changeableProject)
      .then(() => {
        if (updateMakingsOfTrackings) {
          this.updateMakings()
        } else if (this.project && this.project.id) {
          this.fetchProjectOrTrackedTimes(this.project.id, true)
        }
      })
      .catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.timesLoading = false
      })
  }

  private updateMakings () {
    if (!this.project || !this.project.trackedTimes || !this.changeableProject || !this.changeableProject.salary) return
    const newSalary = this.changeableProject.salary
    this.project.trackedTimes.forEach((time) => {
      if (!time || !time.startingTime || !time.endTime || !time.id) return
      const difference = Math.abs(time.endTime - time.startingTime)
      const making = Number((((newSalary / 60) / 60) * (difference / 1000)).toFixed(1))
      return this.timeService.updateMaking(time.id, Number(making))
    })
    if (!this.project.id) return
    this.fetchProjectOrTrackedTimes(this.project.id, true)
  }

  private handleTrackingClicked (tracking: Tracking) {
    if (tracking.id) {
      this.$store.dispatch('tracking/setTracking', tracking)
      this.$router.push({ path: '/timetrack/project/tracking/' + tracking.id })
    }
  }

  private deleteContributorProject () {
    if (!this.project || !this.project.id) return
    this.collaboratorService.getCollaborationIdByCollaboratorProjectId(this.project.id)
      .then((collaboration) => {
        if (!collaboration || !collaboration.id) return
        this.collaboratorService.setCollaboratorState(collaboration.id, CollaborationState.deleted)
          .then(() => {
            this.deleteProject()
          }).catch((error) => {
            this.$store.dispatch('error/setError', error)
          })
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      })
  }
}
